import Phaser from 'phaser';

import { BouncySumsSceneStatus } from '@/features/bouncySums/constants';
import { BouncySumsQuestionGenerator } from '@/features/bouncySums/helpers';
import { BouncySumsSpeedChange } from '@/features/bouncySums/types';
import { EventBus, EVENTS } from '@/features/bouncySums/game/events/bouncySumsEventBus';

import correct01Url from '../../assets/audio/answer/answer-correct-01.ogg?url';
import correct02Url from '../../assets/audio/answer/answer-correct-02.ogg?url';
import correct03Url from '../../assets/audio/answer/answer-correct-03.ogg?url';
import correct04Url from '../../assets/audio/answer/answer-correct-04.ogg?url';
import correct05Url from '../../assets/audio/answer/answer-correct-05.ogg?url';
import correct06Url from '../../assets/audio/answer/answer-correct-06.ogg?url';
import wrongUrl from '../../assets/audio/answer/answer-wrong.ogg?url';
import endJingleUrl from '../../assets/audio/menu/jingle-endgame.ogg?url';
import musicLayer1Url from '../../assets/audio/rhythm-music/110bpm-layer-1.ogg?url';
import musicLayer2Url from '../../assets/audio/rhythm-music/110bpm-layer-2.ogg?url';
import musicLayer3Url from '../../assets/audio/rhythm-music/110bpm-layer-3.ogg?url';
import musicLayer4Url from '../../assets/audio/rhythm-music/110bpm-layer-4.ogg?url';
import musicSfxUrl from '../../assets/audio/rhythm-music/110bpm-sfx.ogg?url';
import pufferfishDefaultDesktopUrl from '../../assets/images/pufferfish-default-desktop.svg?url';
import pufferfishDefaultMobileUrl from '../../assets/images/pufferfish-default-mobile.svg?url';
import pufferfishDefaultTabletUrl from '../../assets/images/pufferfish-default-tablet.svg?url';
import pufferfishRestingDesktopUrl from '../../assets/images/pufferfish-resting-desktop.svg?url';
import pufferfishRestingMobileUrl from '../../assets/images/pufferfish-resting-mobile.svg?url';
import pufferfishRestingTabletUrl from '../../assets/images/pufferfish-resting-tablet.svg?url';
import starfishUrl from '../../assets/images/starfish.svg?url';

import type {
	BouncySumsAnswerRecord,
	BouncySumsConfig,
	BouncySumsQuestion,
	BouncySumsSceneFinishEvent,
} from '@/features/bouncySums/types';

const getPhaserSvgUrl = (url: string) => {
	if (!url.startsWith('data:image/svg+xml') || url.includes(';base64,')) {
		return url;
	}

	const separatorIndex = url.indexOf(',');
	if (separatorIndex === -1) {
		return url;
	}

	const svg = decodeURIComponent(url.slice(separatorIndex + 1));
	const bytes = new TextEncoder().encode(svg);
	let binary = '';

	bytes.forEach((byte) => {
		binary += String.fromCharCode(byte);
	});

	return `data:image/svg+xml;base64,${btoa(binary)}`;
};

export const BOUNCY_SUMS_SCENE_KEY = 'BouncySumsScene';
export const BOUNCY_SUMS_SCENE_WIDTH = 1280;
export const BOUNCY_SUMS_SCENE_HEIGHT = 720;

export interface BouncySumsSceneData {
	config: BouncySumsConfig;
	questions: BouncySumsQuestion[];
	audioOn: boolean;
}

const BEATS_PER_BAR = 4;
const MAX_BARS = 3;
const BEATS_BEFORE_SHOWING_ANSWER = 2;
const COUNTDOWN_BEAT_COUNT = 3;
const QUESTION_REVEAL_PAUSE_BEAT_COUNT = 1;
const QUESTION_REVEAL_STEP_COUNT = 5;
const MIN_BEATS_UNTIL_NEXT_QUESTION = 4;
const INITIAL_COUNTDOWN_BEAT = -1;
const QUESTION_REVEAL_START_BEAT = COUNTDOWN_BEAT_COUNT + QUESTION_REVEAL_PAUSE_BEAT_COUNT;
const QUESTION_ANSWER_VISIBLE_BEAT = QUESTION_REVEAL_START_BEAT + QUESTION_REVEAL_STEP_COUNT - 1;
const ANSWERED_FAST_BEAT = QUESTION_ANSWER_VISIBLE_BEAT + BEATS_PER_BAR + 2;
const HUD_STATE_EMIT_INTERVAL_MS = 100;
const AUDIO_BPM = 110;
const ANSWER_BLOCK_Y = 132;
const ANSWER_FONT_SIZE = 48;
const ANSWER_TOKEN_GAP = 6;
const ANSWER_MAIN_COLOR = '#1b3b37';
const ANSWER_VALUE_COLOR = '#18b692';
const ANSWER_PLACEHOLDER_BLINK_COLOR = '#0dcfa1';
const ANSWER_PLACEHOLDER_ALPHA = 0.72;
const CORRECT_BUBBLE_COUNT = 32;
const SPEED_UP_BUBBLE_COUNT = 48;
const CORRECT_BUBBLE_ANIMATION_DURATION = 1900;
const CORRECT_BUBBLE_MIN_DURATION = 1400;
const CORRECT_BUBBLE_MAX_DURATION = 2200;
const CORRECT_BUBBLE_MIN_RADIUS = 4;
const CORRECT_BUBBLE_MAX_RADIUS = 10;
const CORRECT_BUBBLE_START_X_SPREAD = 88;
const CORRECT_BUBBLE_START_Y_SPREAD = 26;
const CORRECT_BUBBLE_TARGET_X_SPREAD = 360;
const CORRECT_BUBBLE_TARGET_TOP_SPREAD = 190;
const CORRECT_BUBBLE_TARGET_BOTTOM_SPREAD = 130;
const CORRECT_BUBBLE_DEPTH = 30;
const CORRECT_ANSWER_FADE_DELAY = Math.round(CORRECT_BUBBLE_ANIMATION_DURATION / 2);
const CORRECT_ANSWER_FADE_DURATION = 260;
const MOTIVATION_MESSAGE_DELAY = 180;
const MOTIVATION_MESSAGE_VISIBLE_DURATION = 1200;
const CORRECT_ANSWER_FEEDBACK_DURATION = Math.max(
	CORRECT_BUBBLE_ANIMATION_DURATION,
	CORRECT_ANSWER_FADE_DELAY +
		CORRECT_ANSWER_FADE_DURATION +
		MOTIVATION_MESSAGE_DELAY +
		MOTIVATION_MESSAGE_VISIBLE_DURATION,
);
const WRONG_DIGIT_COLOR = '#d64b2a';
const WRONG_DIGIT_FLOAT_DISTANCE = 120;
const WRONG_DIGIT_HORIZONTAL_DRIFT = 28;
const WRONG_DIGIT_FLOAT_DURATION = 1080;
const PUFFERFISH_JUMP_ROTATIONS = [-14, 14];
const WRONG_ANSWER_STOP_JUMP_COUNT = 3;
const PUFFERFISH_IDLE_SCALE_MULTIPLIER = 1.08;
const PUFFERFISH_IDLE_SCALE_DURATION = 560;
const MOBILE_BREAKPOINT = 768;
const DESKTOP_BREAKPOINT = 1024;
const DESKTOP_JUMP_BREAKPOINT = 1280;
const MOBILE_PUFFERFISH_WIDTH = 98;
const MOBILE_PUFFERFISH_HEIGHT = 98;
const TABLET_PUFFERFISH_WIDTH = 104;
const TABLET_PUFFERFISH_HEIGHT = 104;
const DESKTOP_PUFFERFISH_WIDTH = 118;
const DESKTOP_PUFFERFISH_HEIGHT = 122;
const MOBILE_STARFISH_WIDTH = 148;
const MOBILE_STARFISH_HEIGHT = 64;
const DESKTOP_STARFISH_WIDTH = 184;
const DESKTOP_STARFISH_HEIGHT = 81;
const CHARACTER_VERTICAL_OFFSET = 72;
const SHORT_VIEWPORT_HEIGHT = 864;
const SHORT_VIEWPORT_CHARACTER_LIFT_RATIO = 0.75;
const MAX_SHORT_VIEWPORT_CHARACTER_LIFT = 60;
const MOBILE_STARFISH_TOP_OFFSET = 76;
const TABLET_STARFISH_TOP_OFFSET = 80;
const DESKTOP_STARFISH_TOP_OFFSET = 94;
const MOBILE_PUFFERFISH_JUMP_HEIGHT = 153;
const DESKTOP_PUFFERFISH_JUMP_HEIGHT = 200;
const STARFISH_SPRING_DROP = 7;
const STARFISH_SPRING_LIFT = 4;
const STARFISH_WAVE_START_COLOR = 0xaeffec;
const STARFISH_WAVE_END_COLOR = 0x18b692;
const STARFISH_WAVE_START_WIDTH = 270;
const STARFISH_WAVE_START_HEIGHT = 58;
const STARFISH_WAVE_END_WIDTH = 1040;
const STARFISH_WAVE_END_HEIGHT = 236;
const SHORT_VIEWPORT_WAVE_SCALE_RANGE = 160;
const SHORT_VIEWPORT_MIN_WAVE_SCALE = 0.72;
const STARFISH_WAVE_DURATION = 1650;
const STARFISH_WAVE_DEPTH = 9;
const STARFISH_WAVE_FADE_START_PROGRESS = 0.72;
const STARFISH_WAVE_SMOOTHNESS = 96;
const FONT_FAMILY = 'ModernEra, Helvetica, Arial, sans-serif';
const PUFFERFISH_TEXTURE_SIZE = 376;
const END_SCREEN_FADE_DURATION = 300;

type PufferfishPose = 'default' | 'resting';
type BouncySumsScreenSize = 'desktop' | 'mobile' | 'tablet';

type PufferfishTexture = {
	key: string;
	url: string;
};

const ASSET_KEYS = {
	correct: 'bouncy-correct',
	wrong: 'bouncy-wrong',
	endJingle: 'bouncy-end-jingle',
	musicLayer1: 'bouncy-music-layer-1',
	musicLayer2: 'bouncy-music-layer-2',
	musicLayer3: 'bouncy-music-layer-3',
	musicLayer4: 'bouncy-music-layer-4',
	musicSfx: 'bouncy-music-sfx',
	starfish: 'bouncy-starfish',
};

const PUFFERFISH_TEXTURES = {
	desktop: {
		default: {
			key: 'bouncy-pufferfish-default-desktop',
			url: pufferfishDefaultDesktopUrl,
		},
		resting: {
			key: 'bouncy-pufferfish-resting-desktop',
			url: pufferfishRestingDesktopUrl,
		},
	},
	tablet: {
		default: {
			key: 'bouncy-pufferfish-default-tablet',
			url: pufferfishDefaultTabletUrl,
		},
		resting: {
			key: 'bouncy-pufferfish-resting-tablet',
			url: pufferfishRestingTabletUrl,
		},
	},
	mobile: {
		default: {
			key: 'bouncy-pufferfish-default-mobile',
			url: pufferfishDefaultMobileUrl,
		},
		resting: {
			key: 'bouncy-pufferfish-resting-mobile',
			url: pufferfishRestingMobileUrl,
		},
	},
} satisfies Record<BouncySumsScreenSize, Record<PufferfishPose, PufferfishTexture>>;

export default class BouncySumsScene extends Phaser.Scene {
	private configData!: BouncySumsConfig;
	private audioOn = true;
	private isLoaded = false;
	private status: BouncySumsSceneStatus = BouncySumsSceneStatus.START;
	private questionGenerator = new BouncySumsQuestionGenerator();
	private availableQuestions: BouncySumsQuestion[] = [];
	private selectedTables: number[] = [];
	private currentQuestion: BouncySumsQuestion | null = null;
	private currentQuestionIndex = -1;
	private currentAnswer = '';
	private attemptNumber = 0;
	private madeMistake = false;
	private questionAnswered = false;
	private answeringMode = false;
	private slowDown = false;
	private currentBeat = 0;
	private beatsUntilNextQuestion = 0;
	private showAnswerBeat = 0;
	private score = 0;
	private isHighScore = false;
	private previousHighScore = 0;
	private sessionHighScore = 0;
	private currentBpm = 110;
	private pendingBpmSemitoneChange = 0;
	private streak = 0;
	private speedupStreak = 0;
	private answeredCount = 0;
	private startTimeMs = 0;
	private lastAnswerTimeMs = 0;
	private beatEvent?: Phaser.Time.TimerEvent;
	private answers: BouncySumsAnswerRecord[] = [];
	private hasEmittedFinish = false;
	private lastHudStateEmitMs = 0;
	private gameGroup?: Phaser.GameObjects.Container;
	private messageText?: Phaser.GameObjects.Text;
	private answerContainer?: Phaser.GameObjects.Container;
	private answerTextObjects: Phaser.GameObjects.Text[] = [];
	private answerPlaceholderTweens: Phaser.Tweens.Tween[] = [];
	private starfishImage?: Phaser.GameObjects.Image;
	private starfishSpringTweens: Phaser.Tweens.Tween[] = [];
	private starfishWaveTweens: Phaser.Tweens.Tween[] = [];
	private starfishWaveGraphics: Phaser.GameObjects.Graphics[] = [];
	private pufferfishImage?: Phaser.GameObjects.Image;
	private pufferfishJumpTween?: Phaser.Tweens.Tween;
	private pufferfishIdleTween?: Phaser.Tweens.Tween;
	private pufferfishJumpIndex = 0;
	private pufferfishPose: PufferfishPose = 'resting';
	private canJumpPufferfish = true;
	private pufferfishJumpsBeforeStop = 0;
	private music?: Phaser.Sound.BaseSound[];
	private readonly handleScaleResize = () => {
		this.layoutGameObjects();
	};

	constructor() {
		super(BOUNCY_SUMS_SCENE_KEY);
	}

	private loadGame(data: BouncySumsSceneData) {
		this.configData = data.config;
		this.audioOn = data.audioOn;
		this.sound.mute = !data.audioOn;
		this.availableQuestions = data.questions;
		this.selectedTables = this.normalizeSelectedTables(data.config.tables);
		this.currentBpm = data.config.bpm;
		this.sessionHighScore = Math.max(this.sessionHighScore, data.config.highScore);
		this.isLoaded = true;
		this.showStartScreen();
	}

	preload() {
		this.load.audio(ASSET_KEYS.correct, [
			correct01Url,
			correct02Url,
			correct03Url,
			correct04Url,
			correct05Url,
			correct06Url,
		]);
		this.load.audio(ASSET_KEYS.wrong, wrongUrl);
		this.load.audio(ASSET_KEYS.endJingle, endJingleUrl);
		this.load.audio(ASSET_KEYS.musicLayer1, musicLayer1Url);
		this.load.audio(ASSET_KEYS.musicLayer2, musicLayer2Url);
		this.load.audio(ASSET_KEYS.musicLayer3, musicLayer3Url);
		this.load.audio(ASSET_KEYS.musicLayer4, musicLayer4Url);
		this.load.audio(ASSET_KEYS.musicSfx, musicSfxUrl);
		Object.values(PUFFERFISH_TEXTURES).forEach((textures) => {
			Object.values(textures).forEach((texture) => {
				this.load.svg(texture.key, getPhaserSvgUrl(texture.url), {
					width: PUFFERFISH_TEXTURE_SIZE,
					height: PUFFERFISH_TEXTURE_SIZE,
				});
			});
		});
		this.load.svg(ASSET_KEYS.starfish, getPhaserSvgUrl(starfishUrl), {
			width: 438,
			height: 192,
		});
	}

	create() {
		this.sound.mute = !this.audioOn;
		this.bindKeyboardControls();
		this.bindEventBus();
		this.scale.on(Phaser.Scale.Events.RESIZE, this.handleScaleResize);
		this.time.delayedCall(0, () => {
			this.layoutGameObjects();
			EventBus.emit(EVENTS.SCENE_READY);
		});
		this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
			this.scale.off(Phaser.Scale.Events.RESIZE, this.handleScaleResize);
		});
		this.events.once(Phaser.Scenes.Events.DESTROY, () => {
			this.scale.off(Phaser.Scale.Events.RESIZE, this.handleScaleResize);
		});
	}

	update() {
		if (!this.isLoaded || this.status !== BouncySumsSceneStatus.PLAYING) {
			return;
		}

		this.updateHud();
	}

	setAudioEnabled(audioOn: boolean) {
		this.audioOn = audioOn;
		this.sound.mute = !audioOn;
	}

	cancelGame() {
		const packageData = this.buildFinishPackage(false);
		this.stopBeat();
		this.stopMusic();
		this.destroyAnswerBlock();
		this.destroyStarfish();
		this.destroyPufferfish();
		EventBus.emit(EVENTS.CLOSE, packageData);
	}

	private bindEventBus() {
		const handleLoadGame = (data: BouncySumsSceneData) => this.loadGame(data);
		const handleStartGame = (selectedTables?: number[]) => this.startGame(selectedTables);
		const handleAudioChange = (audioOn: boolean) => this.setAudioEnabled(audioOn);
		const handleCancelGame = () => this.cancelGame();
		const handleReturnToStart = () => this.showStartScreen();
		const handleInputDigit = (digit: string) => this.handleDigit(digit);

		EventBus.on(EVENTS.LOAD_GAME, handleLoadGame);
		EventBus.on(EVENTS.START_GAME, handleStartGame);
		EventBus.on(EVENTS.SET_AUDIO_ON, handleAudioChange);
		EventBus.on(EVENTS.CANCEL_GAME, handleCancelGame);
		EventBus.on(EVENTS.RETURN_TO_START, handleReturnToStart);
		EventBus.on(EVENTS.INPUT_DIGIT, handleInputDigit);

		const cleanupSceneListeners = () => {
			EventBus.off(EVENTS.LOAD_GAME, handleLoadGame);
			EventBus.off(EVENTS.START_GAME, handleStartGame);
			EventBus.off(EVENTS.SET_AUDIO_ON, handleAudioChange);
			EventBus.off(EVENTS.CANCEL_GAME, handleCancelGame);
			EventBus.off(EVENTS.RETURN_TO_START, handleReturnToStart);
			EventBus.off(EVENTS.INPUT_DIGIT, handleInputDigit);
			this.destroyAnswerBlock();
			this.destroyStarfish();
			this.destroyPufferfish();
		};

		this.events.once(Phaser.Scenes.Events.SHUTDOWN, cleanupSceneListeners);
		this.events.once(Phaser.Scenes.Events.DESTROY, cleanupSceneListeners);
	}

	private showStartScreen() {
		this.status = BouncySumsSceneStatus.START;
		this.stopBeat();
		this.stopMusic();
		this.clearGameLayers();
		this.currentQuestion = null;
		this.currentAnswer = '';
		this.emitState();
	}

	private startGame(selectedTables = this.selectedTables) {
		const normalizedTables = this.normalizeSelectedTables(selectedTables);
		const selectedQuestions = this.getQuestionsForTables(normalizedTables);

		if (!selectedQuestions.length) {
			return;
		}

		this.selectedTables = normalizedTables;
		this.questionGenerator.loadQuestions(selectedQuestions);
		this.status = BouncySumsSceneStatus.PLAYING;
		this.hasEmittedFinish = false;
		this.previousHighScore = this.getHighScore();
		this.score = 0;
		this.isHighScore = false;
		this.streak = 0;
		this.speedupStreak = 0;
		this.answeredCount = 0;
		this.answers = [];
		this.currentQuestionIndex = -1;
		this.currentBpm = this.getStartingBpm();
		this.pendingBpmSemitoneChange = 0;
		this.pufferfishJumpIndex = 0;
		this.canJumpPufferfish = true;
		this.pufferfishJumpsBeforeStop = 0;
		this.startTimeMs = this.time.now;
		this.lastAnswerTimeMs = this.time.now;
		this.lastHudStateEmitMs = 0;
		this.createGameUi();
		this.showQuestion(this.questionGenerator.getQuestion());
		this.layoutGameObjects();
		this.scheduleBeat();
		this.emitState();
	}

	private getViewportCenterX() {
		return Math.round(this.scale.width / 2);
	}

	private getViewportCenterY() {
		return Math.round(this.scale.height / 2);
	}

	private getRenderScale() {
		const canvasWidth = this.game.canvas.clientWidth || this.scale.width;

		return Math.max(1, this.scale.width / Math.max(1, canvasWidth));
	}

	private toGameSize(value: number) {
		return Math.round(value * this.getRenderScale());
	}

	private toGameFontSize(value: number) {
		return `${this.toGameSize(value)}px`;
	}

	private getCssViewportWidth() {
		return this.scale.width / this.getRenderScale();
	}

	private getCssViewportHeight() {
		return this.scale.height / this.getRenderScale();
	}

	private getScreenSize(): BouncySumsScreenSize {
		const cssViewportWidth = this.getCssViewportWidth();

		if (cssViewportWidth >= DESKTOP_BREAKPOINT) {
			return 'desktop';
		}

		if (cssViewportWidth >= MOBILE_BREAKPOINT) {
			return 'tablet';
		}

		return 'mobile';
	}

	private getPufferfishTextureKey(pose = this.pufferfishPose) {
		return PUFFERFISH_TEXTURES[this.getScreenSize()][pose].key;
	}

	private setPufferfishPose(pose: PufferfishPose) {
		this.pufferfishPose = pose;

		const textureKey = this.getPufferfishTextureKey(pose);

		if (this.pufferfishImage && this.pufferfishImage.texture.key !== textureKey) {
			this.pufferfishImage.setTexture(textureKey);
		}
	}

	private getCharacterVerticalOffset(cssViewportHeight: number) {
		const shortViewportLift = Math.min(
			MAX_SHORT_VIEWPORT_CHARACTER_LIFT,
			Math.max(0, SHORT_VIEWPORT_HEIGHT - cssViewportHeight) * SHORT_VIEWPORT_CHARACTER_LIFT_RATIO,
		);

		return CHARACTER_VERTICAL_OFFSET - shortViewportLift;
	}

	private getShortViewportProgress(cssViewportHeight: number) {
		return Phaser.Math.Clamp((SHORT_VIEWPORT_HEIGHT - cssViewportHeight) / SHORT_VIEWPORT_WAVE_SCALE_RANGE, 0, 1);
	}

	private getStarfishWaveScale() {
		return Phaser.Math.Linear(
			1,
			SHORT_VIEWPORT_MIN_WAVE_SCALE,
			this.getShortViewportProgress(this.getCssViewportHeight()),
		);
	}

	private getCharacterLayout() {
		const cssViewportWidth = this.getCssViewportWidth();
		const cssViewportHeight = this.getCssViewportHeight();
		const screenSize = this.getScreenSize();
		const isDesktopSize = screenSize === 'desktop';
		const pufferfishWidth =
			screenSize === 'desktop'
				? DESKTOP_PUFFERFISH_WIDTH
				: screenSize === 'tablet'
				? TABLET_PUFFERFISH_WIDTH
				: MOBILE_PUFFERFISH_WIDTH;
		const pufferfishHeight =
			screenSize === 'desktop'
				? DESKTOP_PUFFERFISH_HEIGHT
				: screenSize === 'tablet'
				? TABLET_PUFFERFISH_HEIGHT
				: MOBILE_PUFFERFISH_HEIGHT;
		const starfishWidth = isDesktopSize ? DESKTOP_STARFISH_WIDTH : MOBILE_STARFISH_WIDTH;
		const starfishHeight = isDesktopSize ? DESKTOP_STARFISH_HEIGHT : MOBILE_STARFISH_HEIGHT;
		const starfishTopOffset =
			screenSize === 'desktop'
				? DESKTOP_STARFISH_TOP_OFFSET
				: screenSize === 'tablet'
				? TABLET_STARFISH_TOP_OFFSET
				: MOBILE_STARFISH_TOP_OFFSET;
		const characterAreaHeight = Math.max(pufferfishHeight, starfishTopOffset + starfishHeight);
		const pufferfishTop =
			(cssViewportHeight - characterAreaHeight) / 2 + this.getCharacterVerticalOffset(cssViewportHeight);
		const starfishTop = pufferfishTop + starfishTopOffset;

		return {
			pufferfishWidth: this.toGameSize(pufferfishWidth),
			pufferfishHeight: this.toGameSize(pufferfishHeight),
			pufferfishX: this.getViewportCenterX(),
			pufferfishY: this.toGameSize(pufferfishTop + pufferfishHeight / 2),
			starfishWidth: this.toGameSize(starfishWidth),
			starfishHeight: this.toGameSize(starfishHeight),
			starfishX: this.getViewportCenterX(),
			starfishY: this.toGameSize(starfishTop + starfishHeight / 2),
			jumpHeight: this.toGameSize(
				cssViewportWidth >= DESKTOP_JUMP_BREAKPOINT
					? DESKTOP_PUFFERFISH_JUMP_HEIGHT
					: MOBILE_PUFFERFISH_JUMP_HEIGHT,
			),
		};
	}

	private layoutGameObjects() {
		const centerX = this.getViewportCenterX();

		this.answerContainer?.setPosition(centerX, this.toGameSize(ANSWER_BLOCK_Y));
		this.messageText?.setPosition(centerX, this.toGameSize(ANSWER_BLOCK_Y));

		const characterLayout = this.getCharacterLayout();

		this.starfishImage
			?.setPosition(characterLayout.starfishX, characterLayout.starfishY)
			.setDisplaySize(characterLayout.starfishWidth, characterLayout.starfishHeight);
		this.setPufferfishPose(this.pufferfishPose);
		this.pufferfishImage
			?.setPosition(characterLayout.pufferfishX, this.getPufferfishRestingY(characterLayout))
			.setDisplaySize(characterLayout.pufferfishWidth, characterLayout.pufferfishHeight);
	}

	private getPufferfishRestingY(characterLayout: ReturnType<BouncySumsScene['getCharacterLayout']>) {
		if (this.shouldStartPufferfishAtApex()) {
			return characterLayout.pufferfishY - characterLayout.jumpHeight;
		}

		return characterLayout.pufferfishY;
	}

	private shouldStartPufferfishAtApex() {
		return (
			this.status === BouncySumsSceneStatus.PLAYING &&
			this.currentQuestionIndex === 0 &&
			this.pufferfishJumpIndex === 0
		);
	}

	private createGameUi() {
		this.destroyPlayContent();
		this.gameGroup = this.add.container(0, 0).setDepth(10);
		this.createAnswerBlock();
		this.createStarfish();
		this.createPufferfish();

		this.messageText = this.add
			.text(this.getViewportCenterX(), this.toGameSize(ANSWER_BLOCK_Y), '', {
				fontFamily: FONT_FAMILY,
				fontSize: this.toGameFontSize(ANSWER_FONT_SIZE),
				fontStyle: '900',
				color: ANSWER_MAIN_COLOR,
				align: 'center',
				resolution: 1,
			})
			.setOrigin(0.5);

		this.gameGroup.add([this.messageText]);
		this.layoutGameObjects();

		this.updateHud();
	}

	private bindKeyboardControls() {
		this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
			if (!this.isLoaded) {
				return;
			}

			if (event.key === 'Enter' && this.status !== BouncySumsSceneStatus.PLAYING) {
				this.startGame();
				return;
			}

			if (event.key === 'Escape') {
				this.cancelGame();
				return;
			}

			if (event.key === 'Backspace') {
				this.removeLastDigit();
				return;
			}

			if (event.key.length === 1 && /\d/.test(event.key)) {
				this.handleDigit(event.key);
			}
		});
	}

	private showQuestion(question: BouncySumsQuestion) {
		this.currentQuestion = question;
		this.currentQuestionIndex++;
		this.currentAnswer = '';
		this.attemptNumber = 0;
		this.currentBeat = this.currentQuestionIndex === 0 ? INITIAL_COUNTDOWN_BEAT : QUESTION_REVEAL_START_BEAT;
		this.beatsUntilNextQuestion = 0;
		this.showAnswerBeat = 0;
		this.madeMistake = false;
		this.questionAnswered = false;
		this.answeringMode = false;
		this.slowDown = false;
		this.canJumpPufferfish = true;
		this.pufferfishJumpsBeforeStop = 0;
		this.stopPufferfishIdleScaleAnimation(true);
		if (this.answerContainer) {
			this.tweens.killTweensOf(this.answerContainer);
			this.answerContainer.setAlpha(1);
		}
		this.messageText?.setAlpha(1).setText('');
		this.updateQuestionText();
		this.emitState();
	}

	private scheduleBeat() {
		this.stopBeat();
		this.scheduleNextBeat();
	}

	private scheduleNextBeat() {
		this.beatEvent = this.time.addEvent({
			delay: this.getBeatMs(),
			callback: this.handleBeat,
			callbackScope: this,
		});
	}

	private stopBeat() {
		this.beatEvent?.remove(false);
		this.beatEvent = undefined;
	}

	private handleBeat() {
		this.beatEvent = undefined;

		if (this.status !== BouncySumsSceneStatus.PLAYING) {
			return;
		}

		this.applyPendingBpmChange();
		this.currentBeat++;
		const totalBeats = this.getTotalQuestionBeats();
		let didShowNextQuestionOnBeat = false;

		if (this.beatsUntilNextQuestion > 0) {
			this.beatsUntilNextQuestion--;

			if (this.beatsUntilNextQuestion === 0 || this.currentBeat > totalBeats - 2) {
				this.beatsUntilNextQuestion = 0;
				this.showQuestion(this.questionGenerator.getQuestion());
				didShowNextQuestionOnBeat = true;
			}
		}

		if (!didShowNextQuestionOnBeat && !this.questionAnswered && this.currentBeat === totalBeats + 1) {
			this.slowDown = true;
			this.stopPufferfishJumping();
			this.showAnswerBeat = this.currentBeat + BEATS_BEFORE_SHOWING_ANSWER;
			this.messageText?.setText('');
		}

		if (!didShowNextQuestionOnBeat) {
			this.updateQuestionText();
		}

		if (this.currentBeat > 0) {
			this.startMusicIfNeeded();

			if (this.canJumpPufferfish) {
				const shouldStopAfterJump = this.pufferfishJumpsBeforeStop === 1;

				if (this.pufferfishJumpsBeforeStop > 0) {
					this.pufferfishJumpsBeforeStop--;
				}

				this.jumpPufferfish(shouldStopAfterJump);
			}
		}

		if (!this.questionAnswered && this.currentBeat >= QUESTION_REVEAL_START_BEAT) {
			this.answeringMode = true;
		}

		if (this.getProgress() >= 1 && this.currentBeat > totalBeats + BEATS_BEFORE_SHOWING_ANSWER) {
			this.finishGame(true);
			return;
		}

		this.emitState();
		this.scheduleNextBeat();
	}

	private updateQuestionText() {
		if (!this.currentQuestion) {
			this.updateAnswerBlock();
			return;
		}

		this.updateAnswerBlock();
		this.emitState();
	}

	private handleDigit(digit: string) {
		if (
			this.status !== BouncySumsSceneStatus.PLAYING ||
			!this.currentQuestion ||
			this.questionAnswered ||
			!this.answeringMode ||
			!/\d/.test(digit)
		) {
			return;
		}

		const nextAnswer = `${this.currentAnswer}${digit}`;

		if (!this.currentQuestion.answer.startsWith(nextAnswer)) {
			this.attemptNumber++;
			this.madeMistake = true;
			this.streak = 0;
			this.questionGenerator.answeredIncorrect(this.currentQuestion);
			this.stopPufferfishJumpingAfterJumps(WRONG_ANSWER_STOP_JUMP_COUNT);
			this.playSound(ASSET_KEYS.wrong, { volume: 0.75 });
			this.showFloatingText(digit);
			this.cameras.main.shake(120, 0.006);

			this.emitAnswerEvent(false, BouncySumsSpeedChange.None, this.createAnswerRecord(false), nextAnswer);
			return;
		}

		this.currentAnswer = nextAnswer;

		if (this.currentAnswer === this.currentQuestion.answer) {
			this.handleQuestionAnswered();
			return;
		}

		this.updateQuestionText();
	}

	private removeLastDigit() {
		if (this.status !== BouncySumsSceneStatus.PLAYING || this.questionAnswered || !this.currentAnswer.length) {
			return;
		}

		this.currentAnswer = this.currentAnswer.slice(0, -1);
		this.updateQuestionText();
	}

	private clearCurrentAnswer() {
		if (this.status !== BouncySumsSceneStatus.PLAYING || this.questionAnswered || !this.currentAnswer.length) {
			return;
		}

		this.currentAnswer = '';
		this.updateQuestionText();
	}

	private handleQuestionAnswered() {
		if (!this.currentQuestion) {
			return;
		}

		this.attemptNumber++;
		this.questionAnswered = true;
		this.answeringMode = false;
		this.updateQuestionText();

		if (!this.slowDown) {
			this.streak++;
		} else {
			this.streak = 0;
		}

		if (this.currentQuestion.answer && !this.slowDown && this.currentBeat < ANSWERED_FAST_BEAT) {
			this.speedupStreak++;
		} else {
			this.speedupStreak = 0;
		}

		this.answeredCount++;

		let speedChange = BouncySumsSpeedChange.None;
		const streaksNeeded = Math.max(1, Math.round(this.currentBpm / 50 - 1));
		const answeredThreeInARow = this.streak > 0 && this.streak % 3 === 0 && this.speedupStreak === 0;

		if (this.speedupStreak >= streaksNeeded || answeredThreeInARow) {
			this.speedupStreak = 0;
			speedChange = BouncySumsSpeedChange.SpeedUp;
			this.adjustBpm(1);
		}

		this.score += this.currentQuestion.score;

		if (this.slowDown) {
			this.questionGenerator.answeredIncorrect(this.currentQuestion);
		}

		const answerRecord = this.createAnswerRecord();
		this.answers.push(answerRecord);
		this.emitAnswerEvent(true, speedChange, answerRecord);
		this.playCorrectSound();
		this.showCorrectAnswerFeedback(speedChange);
		this.emitState();

		if (this.getProgress() >= 1) {
			this.time.delayedCall(CORRECT_ANSWER_FEEDBACK_DURATION, () => this.finishGame(true));
			return;
		}

		this.beatsUntilNextQuestion = this.getBeatsUntilNextQuestion();
	}

	private emitAnswerEvent(
		isCorrect: boolean,
		speedChange: BouncySumsSpeedChange,
		answerRecord: BouncySumsAnswerRecord,
		currentAnswer = this.currentAnswer,
	) {
		if (!this.currentQuestion) {
			throw new Error('Cannot emit Bouncy Sums answer without a question.');
		}

		EventBus.emit(EVENTS.ANSWER, {
			question: this.currentQuestion,
			questionIndex: this.currentQuestionIndex,
			attemptNumber: this.attemptNumber,
			answerRecord,
			isCorrect,
			madeMistake: this.madeMistake,
			currentAnswer,
			score: Math.round(this.score),
			bpm: Math.round(this.currentBpm),
			speedChange,
		});
	}

	private createAnswerRecord(isCorrect?: boolean): BouncySumsAnswerRecord {
		if (!this.currentQuestion) {
			throw new Error('Cannot create Bouncy Sums answer without a question.');
		}

		const elapsedMs = Math.max(0, Math.round(this.time.now - this.lastAnswerTimeMs));
		const wasAnsweredBeforeReveal = this.currentBeat < this.getTotalQuestionBeats() + BEATS_BEFORE_SHOWING_ANSWER;

		this.lastAnswerTimeMs = this.time.now;

		return {
			answer_time_in_ms: elapsedMs,
			is_correct: isCorrect ?? (!this.madeMistake && wasAnsweredBeforeReveal),
			table: this.currentQuestion.table,
			question: `${this.currentQuestion.question}${this.currentQuestion.answer}`,
			question_id: this.currentQuestion.id,
		};
	}

	private finishGame(finished: boolean) {
		if (this.status === BouncySumsSceneStatus.FINISHED) {
			return;
		}

		this.status = BouncySumsSceneStatus.FINISHED;
		this.stopBeat();
		this.stopMusic();

		const finalScore = Math.round(this.score);

		this.isHighScore = finished && finalScore > this.previousHighScore;
		this.sessionHighScore = Math.max(this.sessionHighScore, finalScore);

		const packageData = this.buildFinishPackage(finished);

		if (!this.hasEmittedFinish) {
			this.hasEmittedFinish = true;
			EventBus.emit(EVENTS.FINISH, packageData);
		}

		this.showEndScreen();
		this.playSound(ASSET_KEYS.endJingle, { volume: 0.65 });
		this.emitState();
	}

	private showEndScreen() {
		const playContent = this.getPlayContentFadeTargets();

		if (playContent.length) {
			this.tweens.add({
				targets: playContent,
				alpha: 0,
				duration: END_SCREEN_FADE_DURATION,
				ease: 'Sine.easeInOut',
				onComplete: () => {
					if (this.status === BouncySumsSceneStatus.FINISHED) {
						this.destroyPlayContent();
					}
				},
			});
			return;
		}

		if (this.status === BouncySumsSceneStatus.FINISHED) {
			this.destroyPlayContent();
		}
	}

	private buildFinishPackage(finished: boolean): BouncySumsSceneFinishEvent {
		return {
			finished,
			correct_count: this.answeredCount,
			bpm: Math.round(this.currentBpm),
			score: Math.round(this.score),
			is_high_score: this.isHighScore,
			previous_high_score: this.previousHighScore,
			selected_tables: this.selectedTables,
			answers: this.answers,
		};
	}

	private emitState() {
		EventBus.emit(EVENTS.STATE_CHANGE, {
			status: this.status,
			score: Math.round(this.score),
			bpm: Math.round(this.currentBpm),
			progress: this.getProgress(),
			timeRemainingSeconds: this.getTimeRemainingSeconds(),
			answeredCount: this.answeredCount,
			isHighScore: this.isHighScore,
			previousHighScore: this.previousHighScore,
			currentQuestion: this.currentQuestion ?? undefined,
		});
	}

	private updateHud() {
		if (this.time.now - this.lastHudStateEmitMs < HUD_STATE_EMIT_INTERVAL_MS) {
			return;
		}

		this.lastHudStateEmitMs = this.time.now;
		this.emitState();
	}

	private getProgress() {
		if (!this.startTimeMs) {
			return 0;
		}

		return Phaser.Math.Clamp((this.time.now - this.startTimeMs) / (this.configData.gameTimeSeconds * 1000), 0, 1);
	}

	private getTimeRemainingSeconds() {
		return Math.max(0, Math.ceil(this.configData.gameTimeSeconds * (1 - this.getProgress())));
	}

	private getTotalQuestionBeats() {
		return QUESTION_ANSWER_VISIBLE_BEAT + BEATS_PER_BAR * (MAX_BARS - 1);
	}

	private getCountdownValue() {
		if (this.currentBeat >= 0 && this.currentBeat < COUNTDOWN_BEAT_COUNT) {
			return COUNTDOWN_BEAT_COUNT - this.currentBeat;
		}

		return null;
	}

	private getQuestionRevealStep() {
		return Phaser.Math.Clamp(this.currentBeat - QUESTION_REVEAL_START_BEAT, 0, QUESTION_REVEAL_STEP_COUNT - 1);
	}

	private isAnswerRevealed() {
		return this.showAnswerBeat > 0 && this.currentBeat > this.showAnswerBeat;
	}

	private getBeatMs(bpm = this.currentBpm) {
		return 60000 / bpm;
	}

	private createAnswerBlock() {
		this.destroyAnswerBlock();

		this.answerContainer = this.add
			.container(this.getViewportCenterX(), this.toGameSize(ANSWER_BLOCK_Y))
			.setDepth(22)
			.setVisible(false);
	}

	private destroyAnswerBlock() {
		this.clearAnswerTextObjects();
		this.answerContainer?.destroy(true);
		this.answerContainer = undefined;
	}

	private clearAnswerTextObjects() {
		this.answerPlaceholderTweens.forEach((tween) => tween.stop());
		this.answerPlaceholderTweens = [];
		this.answerTextObjects.forEach((textObject) => textObject.destroy());
		this.answerTextObjects = [];
	}

	private createAnswerTextObject(text: string, color: string) {
		const textObject = this.add
			.text(0, 0, text, {
				fontFamily: FONT_FAMILY,
				fontSize: this.toGameFontSize(ANSWER_FONT_SIZE),
				fontStyle: '900',
				color,
				align: 'center',
				resolution: 1,
			})
			.setOrigin(0, 0.5);

		this.answerTextObjects.push(textObject);
		this.answerContainer?.add(textObject);

		return textObject;
	}

	private renderAnswerText(mainText: string, answerText = '', shouldBlinkPlaceholders = false) {
		if (!this.answerContainer) {
			return;
		}

		this.clearAnswerTextObjects();

		if (!mainText && !answerText) {
			this.answerContainer.setVisible(false);
			return;
		}

		this.answerContainer.setVisible(true);

		const textSegments: Array<{ textObject: Phaser.GameObjects.Text; gapAfter: number }> = [];
		const tokenGap = this.toGameSize(ANSWER_TOKEN_GAP);

		if (mainText) {
			const mainTextTokens = this.getAnswerTextTokens(mainText);

			mainTextTokens.forEach((textToken, index) => {
				textSegments.push({
					textObject: this.createAnswerTextObject(textToken, ANSWER_MAIN_COLOR),
					gapAfter: index < mainTextTokens.length - 1 || !!answerText ? tokenGap : 0,
				});
			});
		}

		answerText.split('').forEach((character, index) => {
			const isPlaceholder = shouldBlinkPlaceholders && character === '?';
			const isUserInput = index < this.currentAnswer.length && character !== '?';
			const textObject = this.createAnswerTextObject(
				character,
				isUserInput ? ANSWER_MAIN_COLOR : ANSWER_VALUE_COLOR,
			);

			if (isPlaceholder) {
				textObject.setAlpha(ANSWER_PLACEHOLDER_ALPHA);
				const tween = this.tweens.add({
					targets: textObject,
					alpha: 0.46,
					duration: 430,
					delay: index * 90,
					yoyo: true,
					repeat: -1,
					ease: 'Sine.easeInOut',
					onYoyo: () => textObject.setColor(ANSWER_PLACEHOLDER_BLINK_COLOR),
					onRepeat: () => textObject.setColor(ANSWER_VALUE_COLOR),
				});

				this.answerPlaceholderTweens.push(tween);
			}

			textSegments.push({ textObject, gapAfter: 0 });
		});

		const totalWidth = textSegments.reduce(
			(width, { gapAfter, textObject }) => width + textObject.width + gapAfter,
			0,
		);
		let nextX = Math.round(-totalWidth / 2);

		textSegments.forEach(({ gapAfter, textObject }) => {
			textObject.setX(Math.round(nextX));
			nextX += textObject.width + gapAfter;
		});
	}

	private getAnswerTextTokens(text: string) {
		return text.match(/\d+|\D/g) ?? [text];
	}

	private createStarfish() {
		this.destroyStarfish();

		this.starfishImage = this.add.image(0, 0, ASSET_KEYS.starfish).setOrigin(0.5).setDepth(11);
		this.gameGroup?.add(this.starfishImage);
		this.layoutGameObjects();
	}

	private destroyStarfish() {
		this.stopStarfishSpring();
		this.destroyStarfishWaves();
		this.starfishImage?.destroy();
		this.starfishImage = undefined;
	}

	private stopStarfishSpring() {
		this.starfishSpringTweens.forEach((tween) => tween.stop());
		this.starfishSpringTweens = [];
	}

	private createPufferfish() {
		this.destroyPufferfish();
		this.pufferfishPose = 'resting';

		this.pufferfishImage = this.add.image(0, 0, this.getPufferfishTextureKey()).setOrigin(0.5).setDepth(12);
		this.gameGroup?.add(this.pufferfishImage);
		this.layoutGameObjects();
	}

	private destroyPufferfish() {
		this.pufferfishJumpTween?.stop();
		this.pufferfishJumpTween = undefined;
		this.stopPufferfishIdleScaleAnimation();
		this.pufferfishImage?.destroy();
		this.pufferfishImage = undefined;
	}

	private stopPufferfishJumping() {
		this.canJumpPufferfish = false;
		this.pufferfishJumpsBeforeStop = 0;

		this.pufferfishJumpTween?.stop();
		this.pufferfishJumpTween = undefined;
		this.stopPufferfishIdleScaleAnimation();

		if (!this.pufferfishImage) {
			return;
		}

		this.settlePufferfishOnStarfish();
		this.startPufferfishIdleScaleAnimation();
	}

	private stopPufferfishJumpingAfterJumps(jumpCount: number) {
		if (!this.canJumpPufferfish || jumpCount <= 0) {
			return;
		}

		this.pufferfishJumpsBeforeStop = Math.max(this.pufferfishJumpsBeforeStop, jumpCount);
	}

	private settlePufferfishOnStarfish() {
		if (!this.pufferfishImage) {
			return;
		}

		const characterLayout = this.getCharacterLayout();

		this.pufferfishImage
			.setPosition(characterLayout.pufferfishX, characterLayout.pufferfishY)
			.setDisplaySize(characterLayout.pufferfishWidth, characterLayout.pufferfishHeight)
			.setAngle(0);
		this.setPufferfishPose('resting');
	}

	private startPufferfishIdleScaleAnimation() {
		if (!this.pufferfishImage) {
			return;
		}

		const baseScaleX = this.pufferfishImage.scaleX;
		const baseScaleY = this.pufferfishImage.scaleY;

		this.pufferfishIdleTween = this.tweens.add({
			targets: this.pufferfishImage,
			scaleX: baseScaleX * PUFFERFISH_IDLE_SCALE_MULTIPLIER,
			scaleY: baseScaleY * PUFFERFISH_IDLE_SCALE_MULTIPLIER,
			duration: PUFFERFISH_IDLE_SCALE_DURATION,
			ease: 'Sine.easeInOut',
			yoyo: true,
			repeat: -1,
		});
	}

	private stopPufferfishIdleScaleAnimation(shouldResetSize = false) {
		this.pufferfishIdleTween?.stop();
		this.pufferfishIdleTween = undefined;

		if (shouldResetSize && this.pufferfishImage) {
			const characterLayout = this.getCharacterLayout();

			this.pufferfishImage.setDisplaySize(characterLayout.pufferfishWidth, characterLayout.pufferfishHeight);
		}
	}

	private jumpPufferfish(shouldStopAfterJump = false) {
		if (!this.pufferfishImage) {
			if (shouldStopAfterJump) {
				this.stopPufferfishJumping();
			}

			return;
		}

		const rotation = PUFFERFISH_JUMP_ROTATIONS[this.pufferfishJumpIndex % PUFFERFISH_JUMP_ROTATIONS.length];
		const characterLayout = this.getCharacterLayout();
		const duration = Math.round(Math.min(430, this.getBeatMs() * 0.78));
		const isInitialLanding = this.shouldStartPufferfishAtApex();
		this.pufferfishJumpIndex++;

		this.pufferfishJumpTween?.stop();
		this.stopPufferfishIdleScaleAnimation();
		this.setPufferfishPose('default');
		this.pufferfishImage
			.setPosition(
				characterLayout.pufferfishX,
				isInitialLanding
					? characterLayout.pufferfishY - characterLayout.jumpHeight
					: characterLayout.pufferfishY,
			)
			.setDisplaySize(characterLayout.pufferfishWidth, characterLayout.pufferfishHeight)
			.setAngle(isInitialLanding ? rotation : 0);

		if (isInitialLanding) {
			this.pufferfishJumpTween = this.tweens.add({
				targets: this.pufferfishImage,
				y: characterLayout.pufferfishY,
				angle: 0,
				duration: Math.round(duration / 2),
				ease: 'Sine.easeIn',
				onComplete: () => {
					this.pufferfishImage
						?.setPosition(characterLayout.pufferfishX, characterLayout.pufferfishY)
						.setDisplaySize(characterLayout.pufferfishWidth, characterLayout.pufferfishHeight)
						.setAngle(0);
					this.setPufferfishPose('resting');
					this.emitStarfishWave();
					this.springStarfish();
					this.pufferfishJumpTween = undefined;

					if (shouldStopAfterJump) {
						this.stopPufferfishJumping();
					}
				},
			});
			return;
		}

		this.pufferfishJumpTween = this.tweens.add({
			targets: this.pufferfishImage,
			y: characterLayout.pufferfishY - characterLayout.jumpHeight,
			angle: rotation,
			duration: Math.round(duration / 2),
			yoyo: true,
			ease: 'Sine.easeOut',
			onComplete: () => {
				this.pufferfishImage
					?.setPosition(characterLayout.pufferfishX, characterLayout.pufferfishY)
					.setDisplaySize(characterLayout.pufferfishWidth, characterLayout.pufferfishHeight)
					.setAngle(0);
				this.setPufferfishPose('resting');
				this.emitStarfishWave();
				this.springStarfish();
				this.pufferfishJumpTween = undefined;

				if (shouldStopAfterJump) {
					this.stopPufferfishJumping();
				}
			},
		});
	}

	private springStarfish() {
		const starfishImage = this.starfishImage;

		if (!starfishImage) {
			return;
		}

		this.stopStarfishSpring();

		const characterLayout = this.getCharacterLayout();
		const springDrop = this.toGameSize(STARFISH_SPRING_DROP);
		const springLift = this.toGameSize(STARFISH_SPRING_LIFT);
		const compressDuration = Math.round(Math.min(110, this.getBeatMs() * 0.18));
		const reboundDuration = Math.round(Math.min(150, this.getBeatMs() * 0.24));
		const settleDuration = Math.round(Math.min(170, this.getBeatMs() * 0.3));

		starfishImage
			.setPosition(characterLayout.starfishX, characterLayout.starfishY)
			.setDisplaySize(characterLayout.starfishWidth, characterLayout.starfishHeight);

		const baseScaleX = starfishImage.scaleX;
		const baseScaleY = starfishImage.scaleY;

		const settleStarfish = () => {
			starfishImage
				.setPosition(characterLayout.starfishX, characterLayout.starfishY)
				.setScale(baseScaleX, baseScaleY)
				.setAngle(0);
			this.starfishSpringTweens = [];
		};

		const settle = () => {
			const settleTween = this.tweens.add({
				targets: starfishImage,
				y: characterLayout.starfishY,
				scaleX: baseScaleX,
				scaleY: baseScaleY,
				duration: settleDuration,
				ease: 'Back.easeOut',
				onComplete: settleStarfish,
			});

			this.starfishSpringTweens = [settleTween];
		};

		const rebound = () => {
			const reboundTween = this.tweens.add({
				targets: starfishImage,
				y: characterLayout.starfishY - springLift,
				scaleX: baseScaleX * 0.96,
				scaleY: baseScaleY * 1.1,
				duration: reboundDuration,
				ease: 'Sine.easeOut',
				onComplete: settle,
			});

			this.starfishSpringTweens = [reboundTween];
		};

		const compressTween = this.tweens.add({
			targets: starfishImage,
			y: characterLayout.starfishY + springDrop,
			scaleX: baseScaleX * 1.14,
			scaleY: baseScaleY * 0.78,
			duration: compressDuration,
			ease: 'Sine.easeIn',
			onComplete: rebound,
		});

		this.starfishSpringTweens = [compressTween];
	}

	private emitStarfishWave() {
		if (!this.starfishImage) {
			return;
		}

		const characterLayout = this.getCharacterLayout();
		const progress = { value: 0, maxValue: 0 };
		const wave = this.add.graphics().setDepth(STARFISH_WAVE_DEPTH);
		this.starfishWaveGraphics.push(wave);
		this.drawStarfishWave(wave, characterLayout, progress.value);

		const tween = this.tweens.add({
			targets: progress,
			value: 1,
			duration: STARFISH_WAVE_DURATION,
			ease: 'Sine.easeOut',
			onUpdate: () => {
				progress.maxValue = Math.max(progress.maxValue, progress.value);
				this.drawStarfishWave(wave, characterLayout, progress.maxValue);
			},
			onComplete: () => {
				wave.destroy();
				this.starfishWaveGraphics = this.starfishWaveGraphics.filter((item) => item !== wave);
				this.starfishWaveTweens = this.starfishWaveTweens.filter((item) => item !== tween);
			},
		});

		this.starfishWaveTweens.push(tween);
	}

	private drawStarfishWave(
		wave: Phaser.GameObjects.Graphics,
		characterLayout: ReturnType<BouncySumsScene['getCharacterLayout']>,
		progress: number,
	) {
		const waveScale = this.getStarfishWaveScale();
		const width = Phaser.Math.Linear(
			this.toGameSize(STARFISH_WAVE_START_WIDTH * waveScale),
			this.toGameSize(STARFISH_WAVE_END_WIDTH * waveScale),
			progress,
		);
		const height = Phaser.Math.Linear(
			this.toGameSize(STARFISH_WAVE_START_HEIGHT * waveScale),
			this.toGameSize(STARFISH_WAVE_END_HEIGHT * waveScale),
			progress,
		);
		const y = characterLayout.starfishY + characterLayout.starfishHeight * 0.08;
		const color = this.interpolateColor(STARFISH_WAVE_START_COLOR, STARFISH_WAVE_END_COLOR, progress);
		const alpha =
			progress <= STARFISH_WAVE_FADE_START_PROGRESS
				? 1
				: Phaser.Math.Clamp(
						1 - (progress - STARFISH_WAVE_FADE_START_PROGRESS) / (1 - STARFISH_WAVE_FADE_START_PROGRESS),
						0,
						1,
				  );

		wave.clear();
		wave.setDepth(STARFISH_WAVE_DEPTH);
		wave.fillStyle(color, alpha);
		wave.fillEllipse(characterLayout.starfishX, y, width, height, STARFISH_WAVE_SMOOTHNESS);
	}

	private destroyStarfishWaves() {
		this.starfishWaveTweens.forEach((tween) => tween.stop());
		this.starfishWaveTweens = [];
		this.starfishWaveGraphics.forEach((wave) => wave.destroy());
		this.starfishWaveGraphics = [];
	}

	private interpolateColor(startColor: number, endColor: number, progress: number) {
		const start = Phaser.Display.Color.IntegerToColor(startColor);
		const end = Phaser.Display.Color.IntegerToColor(endColor);
		const red = Math.round(Phaser.Math.Linear(start.red, end.red, progress));
		const green = Math.round(Phaser.Math.Linear(start.green, end.green, progress));
		const blue = Math.round(Phaser.Math.Linear(start.blue, end.blue, progress));

		return Phaser.Display.Color.GetColor(red, green, blue);
	}

	private updateAnswerBlock() {
		if (!this.answerContainer) {
			return;
		}

		if (this.status !== BouncySumsSceneStatus.PLAYING || !this.currentQuestion) {
			this.renderAnswerText('');
			return;
		}

		const countdownValue = this.getCountdownValue();

		if (countdownValue !== null) {
			this.renderAnswerText(String(countdownValue));
			return;
		}

		if (this.currentBeat < QUESTION_REVEAL_START_BEAT) {
			this.renderAnswerText('');
			return;
		}

		if (this.questionAnswered) {
			this.renderAnswerText(
				this.getQuestionTextForRevealStep(QUESTION_REVEAL_STEP_COUNT - 1),
				this.currentAnswer,
			);
			return;
		}

		const questionRevealStep = this.getQuestionRevealStep();

		if (questionRevealStep < QUESTION_REVEAL_STEP_COUNT - 1) {
			this.renderAnswerText(this.getQuestionTextForRevealStep(questionRevealStep));
			return;
		}

		const isAnswerRevealed = this.isAnswerRevealed();
		const answerText = isAnswerRevealed
			? this.currentQuestion.answer
			: this.currentQuestion.answer
					.split('')
					.map((digit, index) => (index < this.currentAnswer.length ? digit : '?'))
					.join('');

		this.renderAnswerText(this.getQuestionTextForRevealStep(questionRevealStep), answerText, !isAnswerRevealed);
	}

	private getQuestionTextForRevealStep(revealStep: number) {
		const questionText = this.currentQuestion?.question ?? '';
		const match = questionText.match(/^(\d+)([xX*×])(\d+)(=?)$/);

		if (!match) {
			return questionText;
		}

		const [, firstNumber, productSign, secondNumber, equalsSign] = match;

		if (revealStep <= 0) {
			return firstNumber;
		}

		if (revealStep === 1) {
			return `${firstNumber}${productSign}`;
		}

		if (revealStep === 2) {
			return `${firstNumber}${productSign}${secondNumber}`;
		}

		if (revealStep === 3) {
			return `${firstNumber}${productSign}${secondNumber}${equalsSign}`;
		}

		return questionText;
	}

	private adjustBpm(semitones: number) {
		this.pendingBpmSemitoneChange += semitones;
	}

	private applyPendingBpmChange() {
		if (this.pendingBpmSemitoneChange === 0) {
			return;
		}

		this.currentBpm = this.getEffectiveBpm();
		this.pendingBpmSemitoneChange = 0;
		this.updateMusicRate();
	}

	private getBeatsUntilNextQuestion() {
		return Math.max(
			MIN_BEATS_UNTIL_NEXT_QUESTION,
			Math.ceil(CORRECT_ANSWER_FEEDBACK_DURATION / this.getBeatMs(this.getEffectiveBpm())),
		);
	}

	private getEffectiveBpm() {
		return this.currentBpm * Math.pow(2, this.pendingBpmSemitoneChange / 12);
	}

	private getStartingBpm() {
		return this.configData.bpm;
	}

	private getMusicRate() {
		return this.currentBpm / AUDIO_BPM;
	}

	private normalizeSelectedTables(tables?: number[]) {
		const normalizedTables = [...new Set(tables ?? [])]
			.filter((item) => Number.isInteger(item) && item >= 1 && item <= 10)
			.sort((a, b) => a - b);

		if (normalizedTables.length) {
			return normalizedTables;
		}

		return [...this.configData.tables];
	}

	private getQuestionsForTables(tables: number[]) {
		const tableSet = new Set(tables.map(String));

		return this.availableQuestions.filter((question) => tableSet.has(question.table));
	}

	private getHighScore() {
		return Math.max(0, this.configData.highScore, this.sessionHighScore);
	}

	private playMusic() {
		this.stopMusic();
		this.music = [
			this.sound.add(ASSET_KEYS.musicLayer1, { loop: true, rate: this.getMusicRate(), volume: 0.36 }),
			this.sound.add(ASSET_KEYS.musicLayer2, { loop: true, rate: this.getMusicRate(), volume: 0.16 }),
			this.sound.add(ASSET_KEYS.musicLayer3, { loop: true, rate: this.getMusicRate(), volume: 0.12 }),
			this.sound.add(ASSET_KEYS.musicLayer4, { loop: true, rate: this.getMusicRate(), volume: 0.1 }),
			this.sound.add(ASSET_KEYS.musicSfx, { loop: true, rate: this.getMusicRate(), volume: 0.16 }),
		];
		this.music.forEach((sound) => sound.play());
	}

	private startMusicIfNeeded() {
		if (this.music) {
			return;
		}

		this.playMusic();
	}

	private updateMusicRate() {
		this.music?.forEach((sound) => {
			if ('setRate' in sound && typeof sound.setRate === 'function') {
				sound.setRate(this.getMusicRate());
			}
		});
	}

	private stopMusic() {
		this.music?.forEach((sound) => {
			if (sound.isPlaying) {
				sound.stop();
			}

			this.sound.remove(sound);
		});
		this.music = undefined;
	}

	private playCorrectSound() {
		this.playSound(ASSET_KEYS.correct, { volume: 0.58 });
	}

	private playSound(key: string, config?: Phaser.Types.Sound.SoundConfig) {
		if (!this.audioOn) {
			return;
		}

		this.sound.play(key, config);
	}

	private showCorrectAnswerFeedback(speedChange: BouncySumsSpeedChange) {
		this.emitCorrectAnswerBubbles(
			speedChange === BouncySumsSpeedChange.SpeedUp ? SPEED_UP_BUBBLE_COUNT : CORRECT_BUBBLE_COUNT,
		);

		this.time.delayedCall(CORRECT_ANSWER_FADE_DELAY, () => {
			if (this.status !== BouncySumsSceneStatus.PLAYING || !this.questionAnswered) {
				return;
			}

			this.fadeOutAnswerBlock(() => {
				this.time.delayedCall(MOTIVATION_MESSAGE_DELAY, () => this.showCorrectAnswerMessage(speedChange));
			});
		});
	}

	private fadeOutAnswerBlock(onComplete: () => void) {
		if (!this.answerContainer) {
			onComplete();
			return;
		}

		this.tweens.killTweensOf(this.answerContainer);
		this.tweens.add({
			targets: this.answerContainer,
			alpha: 0,
			duration: CORRECT_ANSWER_FADE_DURATION,
			ease: 'Sine.easeInOut',
			onComplete,
		});
	}

	private showCorrectAnswerMessage(speedChange: BouncySumsSpeedChange) {
		if (this.status !== BouncySumsSceneStatus.PLAYING || !this.questionAnswered) {
			return;
		}

		const message = this.getCorrectAnswerMessage(speedChange);

		if (!message) {
			return;
		}

		this.messageText?.setAlpha(1).setText(message);
	}

	private getCorrectAnswerMessage(speedChange: BouncySumsSpeedChange) {
		if (speedChange === BouncySumsSpeedChange.SpeedUp) {
			return this.configData.speedupMessage;
		}

		const messages = this.configData.motivationalMessages;

		if (!messages.length) {
			return '';
		}

		return Phaser.Utils.Array.GetRandom(messages);
	}

	private emitCorrectAnswerBubbles(count: number) {
		const origin = this.getCorrectAnswerBubbleOrigin();

		for (let index = 0; index < count; index++) {
			const radius = this.toGameSize(Phaser.Math.Between(CORRECT_BUBBLE_MIN_RADIUS, CORRECT_BUBBLE_MAX_RADIUS));
			const startX =
				origin.x +
				this.toGameSize(Phaser.Math.Between(-CORRECT_BUBBLE_START_X_SPREAD, CORRECT_BUBBLE_START_X_SPREAD));
			const startY =
				origin.y +
				this.toGameSize(Phaser.Math.Between(-CORRECT_BUBBLE_START_Y_SPREAD, CORRECT_BUBBLE_START_Y_SPREAD));
			const burstAngle = Phaser.Math.FloatBetween(0, Math.PI * 2);
			const burstDistanceX = this.toGameSize(Phaser.Math.Between(96, CORRECT_BUBBLE_TARGET_X_SPREAD));
			const burstDistanceY = this.toGameSize(Phaser.Math.Between(52, CORRECT_BUBBLE_TARGET_TOP_SPREAD));
			const particle = this.add
				.circle(startX, startY, radius, 0xffffff, 1)
				.setDepth(CORRECT_BUBBLE_DEPTH)
				.setScale(Phaser.Math.FloatBetween(0.24, 0.46));
			const targetX = startX + Math.cos(burstAngle) * burstDistanceX;
			const targetY =
				startY +
				Math.sin(burstAngle) *
					Math.min(
						burstDistanceY,
						this.toGameSize(
							Math.sin(burstAngle) < 0
								? CORRECT_BUBBLE_TARGET_TOP_SPREAD
								: CORRECT_BUBBLE_TARGET_BOTTOM_SPREAD,
						),
					);

			this.tweens.add({
				targets: particle,
				x: targetX,
				y: targetY,
				alpha: 0,
				scale: Phaser.Math.FloatBetween(1.1, 1.7),
				duration: Phaser.Math.Between(CORRECT_BUBBLE_MIN_DURATION, CORRECT_BUBBLE_MAX_DURATION),
				delay: Phaser.Math.Between(0, 170),
				ease: 'Sine.easeOut',
				onComplete: () => particle.destroy(),
			});
		}
	}

	private getCorrectAnswerBubbleOrigin() {
		return {
			x: Math.round(this.answerContainer?.x ?? this.getViewportCenterX()),
			y: Math.round(this.answerContainer?.y ?? this.toGameSize(ANSWER_BLOCK_Y)),
		};
	}

	private showFloatingText(digit: string) {
		const { x, y } = this.getAnswerInputOrigin();
		const text = this.add
			.text(x, y, digit, {
				fontFamily: FONT_FAMILY,
				fontSize: this.toGameFontSize(ANSWER_FONT_SIZE),
				fontStyle: '900',
				color: WRONG_DIGIT_COLOR,
			})
			.setOrigin(0.5)
			.setDepth(25)
			.setRotation(Phaser.Math.FloatBetween(-0.28, 0.28));

		this.tweens.add({
			targets: text,
			x: x + this.toGameSize(Phaser.Math.Between(-WRONG_DIGIT_HORIZONTAL_DRIFT, WRONG_DIGIT_HORIZONTAL_DRIFT)),
			y: y - this.toGameSize(WRONG_DIGIT_FLOAT_DISTANCE),
			alpha: 0,
			rotation: text.rotation + Phaser.Math.FloatBetween(-0.45, 0.45),
			duration: WRONG_DIGIT_FLOAT_DURATION,
			ease: 'Cubic.easeOut',
			onComplete: () => text.destroy(),
		});
	}

	private getAnswerInputOrigin() {
		const fallbackPosition = {
			x: this.getViewportCenterX(),
			y: this.toGameSize(ANSWER_BLOCK_Y),
		};

		if (!this.answerContainer || !this.currentQuestion?.answer.length) {
			return fallbackPosition;
		}

		const answerSlotObjects = this.answerTextObjects.slice(-this.currentQuestion.answer.length);
		const slotIndex = Math.min(this.currentAnswer.length, Math.max(0, answerSlotObjects.length - 1));
		const slotObject = answerSlotObjects[slotIndex];

		if (!slotObject) {
			return {
				x: Math.round(this.answerContainer.x),
				y: Math.round(this.answerContainer.y),
			};
		}

		return {
			x: Math.round(this.answerContainer.x + slotObject.x + slotObject.width / 2),
			y: Math.round(this.answerContainer.y + slotObject.y),
		};
	}

	private getPlayContentFadeTargets() {
		return [this.gameGroup, this.answerContainer, ...this.starfishWaveGraphics].filter(Boolean);
	}

	private destroyPlayContent() {
		this.gameGroup?.destroy(true);
		this.gameGroup = undefined;
		this.messageText = undefined;
		this.destroyAnswerBlock();
		this.destroyStarfish();
		this.destroyPufferfish();
	}

	private clearGameLayers() {
		this.destroyPlayContent();
	}
}
